import { Checkbox, Popconfirm } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { Tag, Trash2, UserX } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Cta from '../../../components/buttons/Cta';
import LoadingSpinner from '../../../components/buttons/LoadingSpinner';
import AddCouponForm from '../../../components/forms/coupon/AddCouponForm';
import SearchTable from '../../../components/inputs/search/SearchTable';
import PopoverForm from '../../../components/popover/PopoverForm';
import Table from '../../../components/tables/Table';
import TableItem from '../../../components/tables/TableItem';
import Typography from '../../../components/typography/Typography';
import { CouponInterface } from '../../../interfaces/Coupons';
import { CouponService } from '../../../services/coupons/CouponService';
import { GroupsService } from '../../../services/groups/Groups';
import { UsersService } from '../../../services/users/Users';
import { setRootAside } from '../../../state/component/AsideSlice';
import { deleteCoupon, setCoupons } from '../../../state/coupon/CouponSlice';
import { RootState } from '../../../state/store';
import { addToast } from '../../../state/toast/ToastSlice';
import {
  CompanyModules,
  hasModule,
} from '../../../utils/auth/AuthorizationUtility';
import CurrencyFormatter from '../../../utils/text/CurrencyFormatter';

type Group = {
  group: any;
};

const GroupMembersContainer: FC<Group> = ({ group }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const service = new GroupsService();
  const couponService = new CouponService();
  const userService = new UsersService();
  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );
  const modules = useSelector((state: RootState) => state.auth.modules);
  const dispatch = useDispatch();

  const fetchUsers = async () => {
    setLoading(true);
    const users = await service.getUsersFromGroupById(
      companyId,
      group.bedGr_id
    );
    setUsers(users ? users : []);
    const extractedCoupons = users.map((user: any) => user.coupon);
    dispatch(setCoupons(extractedCoupons));
    setLoading(false);
  };

  const onDelete = async (couponId: number, userId: number) => {
    setConfirmLoading(true);
    await couponService.deleteCoupon(companyId, couponId);

    users.find((user: any) => user.geb_id === userId).coupon = null;

    dispatch(deleteCoupon(userId));

    setConfirmLoading(false);
    dispatch(
      addToast({
        description: 'De coupon is verwijderd.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const onRemoveUser = async (userId: number) => {
    setConfirmLoading(true);
    toggleUser(userId);
    await userService.removeFromGroup(companyId, userId);

    setUsers((prevUsers) => prevUsers.filter((user) => user.geb_id !== userId));

    setConfirmLoading(false);
    dispatch(
      addToast({
        description: 'De gebruiker is uit de groep verwijderd.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const createCouponsGroup = async () => {
    setLoading(true);
    await couponService.createCouponsGroup(
      companyId,
      selectedUserIds,
      group.bedGr_bedrag
    );

    // dispatch(setCoupons([...coupons, ...createdCoupons]));
    setSelectedUserIds([]);
    fetchUsers();

    setLoading(false);
    dispatch(
      addToast({
        description: 'De coupons zijn aangemaakt.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const viewDetail = (user: string[]) => {
    dispatch(
      setRootAside({
        component: 'userDetail',
        data: { user: user },
        title: 'Gebruiker',
      })
    );
  };

  const handleCouponSubmit = (coupon: CouponInterface) => {
    console.log('Form values:', coupon);
    const userId = coupon.cou_geb_id;
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.geb_id === userId ? { ...user, coupon: coupon } : user
      )
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredUsers = users.filter((user) => {
    return (
      user.geb_voornaam.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.geb_naam.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  useEffect(() => {
    if (companyId) {
      fetchUsers();
    }
  }, [group.bedGr_id]);

  const tableHeaders: object = [
    'Naam',
    ...(hasModule(modules, CompanyModules.CYOD)
      ? ['Coupon', 'Bedrag', 'Resterend bedrag']
      : []),
    '',
  ];

  const allSelected =
    users.length > 0 && selectedUserIds.length === users.length;

  const isSelected = (id: number) => selectedUserIds.includes(id);
  const toggleUser = (id: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
    );
  };
  const indeterminate =
    selectedUserIds.length > 0 && selectedUserIds.length < users.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map((u) => u.geb_id));
    }
  };

  return (
    <div className="mr-5">
      {loading ? (
        <LoadingSpinner />
      ) : users.length === 0 ? (
        <div className="my-3">
          <Typography tag={'p'} type={'default'}>
            Geen gebruikers in deze groep, ga naar het gebruikers tabblad om
            gebruikers toe te voegen.
          </Typography>
        </div>
      ) : (
        <>
          <SearchTable
            handleChange={handleSearchChange}
            placeholder="Zoek gebruiker..."
            value={searchQuery}
          />
          <Table
            header={tableHeaders}
            selectAll={toggleAll}
            allSelected={allSelected}
            selectIndeterminate={indeterminate}
          >
            {filteredUsers.map((user) => (
              <tr key={user.geb_id}>
                <TableItem>
                  <Checkbox
                    checked={isSelected(user.geb_id)}
                    onChange={() => toggleUser(user.geb_id)}
                  />
                </TableItem>
                <TableItem>
                  <div
                    className="cursor-pointer"
                    onClick={() => viewDetail(user)}
                  >
                    <div>
                      {user.geb_voornaam} {user.geb_naam}
                    </div>
                    <Typography tag={'p'} type={'label'}>
                      {user.email}
                    </Typography>
                  </div>
                </TableItem>
                {hasModule(modules, CompanyModules.CYOD) && (
                  <>
                    <TableItem>
                      {user.coupon ? (
                        <Popconfirm
                          title={t('Verwijder coupon')}
                          description={t('Wilt u deze coupon verwijderen?')}
                          onConfirm={() =>
                            onDelete(user.coupon?.cou_id as number, user.geb_id)
                          }
                          okText={t('Verwijder')}
                          cancelText={t('Annuleer')}
                          okButtonProps={{ loading: confirmLoading }}
                          okType="danger"
                          icon={null}
                        >
                          <button className="text-red-700 hover:text-red-800">
                            <Trash2 size={18} />
                          </button>
                        </Popconfirm>
                      ) : (
                        <PopoverForm
                          form={
                            <AddCouponForm
                              user={user}
                              callback={handleCouponSubmit}
                            />
                          }
                          button={
                            <button className="hover:text-blue-700">
                              <Tag size={18} />
                            </button>
                          }
                          title="Coupon toevoegen"
                        />
                      )}
                    </TableItem>
                    <TableItem>
                      <CurrencyFormatter
                        tag="excl. BTW"
                        price={user.coupon?.cou_initial_value}
                      />
                      <Typography tag="p" type="smallest">
                        {user.coupon?.cou_code}
                      </Typography>
                    </TableItem>
                    <TableItem>
                      <CurrencyFormatter
                        tag="excl. BTW"
                        price={user.coupon?.cou_bedrag}
                      />
                    </TableItem>
                  </>
                )}
                <TableItem>
                  <Popconfirm
                    title={t('Verwijder gebruiker')}
                    description={t(
                      'Wilt u deze gebruiker uit de groep verwijderen?'
                    )}
                    onConfirm={() => onRemoveUser(user.geb_id)}
                    okText={t('Verwijder')}
                    cancelText={t('Annuleer')}
                    okType="danger"
                    icon={null}
                  >
                    <button className="hover:text-red-800">
                      <UserX size={18} />
                    </button>
                  </Popconfirm>
                </TableItem>
              </tr>
            ))}
          </Table>
          {hasModule(modules, CompanyModules.CYOD) &&
            selectedUserIds.length > 0 && (
              <div className="my-3 flex flex-col mt-5">
                <div>
                  <Typography tag="p" type="label">
                    Maak voor iedere gebruiker in deze groep een CYOD coupon aan
                  </Typography>
                  <Typography tag="p" type="label">
                    Er wordt een coupon aangemaakt met een waarde van{' '}
                    <strong>
                      €
                      {group.bedGr_bedrag
                        ? group.bedGr_bedrag.toFixed(2)
                        : '0.00'}{' '}
                      excl. BTW
                    </strong>
                    .
                  </Typography>
                  <Typography tag="p" type="label" color="danger">
                    Bestaande coupons worden verwijderd
                  </Typography>
                </div>
                <div className="w-fit">
                  <Cta
                    text={`${selectedUserIds.length} coupons aanmaken`}
                    onClick={createCouponsGroup}
                  />
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default GroupMembersContainer;
